import { useContext, useEffect, useRef, useState } from 'react'
import { createSearchParams, useParams } from 'react-router-dom'
import {
  DeviceContext,
  SET_DEVICES_DATA,
  SET_SELECTED_DEVICES_DATA,
  SET_SHOW_HIDE_DEVICES_POPUP,
} from './context/context'
import { NavContext } from '../../../context/global_context'
import InputText from '../../common/InputText'
import NoRecordsFound from '../../common/NoRecordsFound'
import Pagination from '../../common/Pagination'
import Button from '../../common/Button'
import LinkWithStatus from '../../common/LinkWithStatus'
import { deleteRequest, getRequest } from '../../../services/base'
import DevicesRow from './DevicesRow'
import DeviceForm from './form'
import ConfirmationPopup from '../../common/ConfirmationAlert'
import { downloadFileBlob } from '../../../utils/common'
import { getSessionStorageData } from '../../../utils/SessionStorage'

const Devices = () => {
  let { vehicle } = useParams()
  const [state, dispatch] = useContext(DeviceContext)
  const [navState, navDispatch] = useContext(NavContext)
  const [searchValue, setSearchValue] = useState('')
  const [deviceToDelete, setDeviceToDelete] = useState({})
  const [deleteMsg, setDeleteMsg] = useState('')

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`
  const currentUrl = useRef(
    `/api/devices/?vehicle=${vehicle}&search=${searchValue}`
  )

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: 'Admin Tools',
        sub_header: `Vehicle List / ${vehicle} / Devices`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getDevicesData()
  }, [])

  useEffect(() => {
    if (deviceToDelete?.id) {
      let msg = []
      if (deviceToDelete.has_mapping?.has_wire) {
        msg.push('Wire')
      }
      if (deviceToDelete.has_mapping?.has_dtc) {
        msg.push('Dtc')
      }
      if (deviceToDelete.has_mapping?.has_connector) {
        msg.push('Connector')
      }
      if (deviceToDelete.has_mapping?.has_device_connection) {
        msg.push('Device Connection')
      }

      if (msg.length > 0) {
        setDeleteMsg(
          `Selected Device have mapping with ${msg.join(',')} so please make sure if you click on ok then it will remove this mapping too.`
        )
      } else {
        setDeleteMsg(
          `You can delete selected device, it don't have any mapping.`
        )
      }
    }
  }, [deviceToDelete])

  const getDevicesData = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: 'Fetching Devices Data',
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(currentUrl.current)
    dispatch({
      type: SET_DEVICES_DATA,
      data: { data: res.results, pagination: res.pagination },
    })
    navDispatch({
      type: 'loader',
      data: {},
      notification: {},
    })
  }

  const deleteDevice = async (device) => {
    if (
      window.confirm(
        `Are you sure want to delete ${device.name}, It will delete all existing mappings`
      ) === true
    ) {
      let res = await deleteRequest(
        `/api/devices/${device?.id}/?vehicle=${vehicle}`
      )
      setDeviceToDelete({})
      if (res?.status) {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: `${device.name} Device Successfully deleted!`,
          },
        })
        setTimeout(() => {
          getDevicesData()
        }, 100)
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: `Something went wrong!`,
          },
        })
      }
    }
  }

  const onDataSave = (status, msg) => {
    if (status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: msg,
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: msg,
        },
      })
    }
  }

  const downloadGenerateDefaultSvg = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: 'Downloading Default SVG Data',
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(
      `/api/devices/download/${vehicle}/?vehicle=${vehicle}`,
      true,
      true
    )

    downloadFileBlob(res, `default_svg.zip`)

    navDispatch({
      type: 'loader',
      data: {},
      notification: {},
    })
  }

  return (
    <div className="w-full">
      <DeviceForm
        onDataSave={(status, msg) => {
          onDataSave(status, msg)
        }}
      />
      <form
        className="flex flex-col w-full space-y-2 pb-2"
        acceptCharset="UTF-8"
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <div
          className={`flex space-x-5 justify-between content-center items-center`}
        >
          <div className="flex items-center w-1/2">
            <div className="flex items-center w-full">
              <InputText
                name="search_value"
                placeholder={`Search Devices`}
                className={`w-100`}
                value={searchValue}
                isFullSize={true}
                type="search"
                onChange={(value) => {
                  setSearchValue(value)
                }}
                isRequired={false}
                wrapperClass={`pl-0`}
              />
              <Button
                className={`cursor-pointer btn my-4 justify-end`}
                text={`Search`}
                onClick={() => {
                  currentUrl.current = `/api/devices/?vehicle=${vehicle}&search=${searchValue}`
                  getDevicesData()
                }}
              />
            </div>
          </div>
          <div className="w-6/12 flex font-light text-center space-x-5 justify-end content-center items-center">
            <LinkWithStatus
              href={`/administration/devices/download`}
              className={
                'cursor-pointer inline-block bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded'
              }
              title="Generate Default SVG"
              onClickFunc={() => {
                downloadGenerateDefaultSvg()
              }}
            />
            <LinkWithStatus
              href={`/administration/devices/new`}
              className={
                'cursor-pointer inline-block bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded'
              }
              title="Create New Devices"
              onClickFunc={() => {
                dispatch({
                  type: SET_SHOW_HIDE_DEVICES_POPUP,
                  data: true,
                })
              }}
            />
          </div>
        </div>
      </form>
      <div className="font-light">
        <div className="flex flex-row w-full bg-tracer-lt-blue rounded-t-lg text-white px-3 text-sm">
          <div className="flex w-3/12 items-center">Name</div>
          <div className="flex w-2/12 pl-6 items-center">Category</div>
          <div className="flex w-4/12 pl-6 items-center">Description</div>
          <div className="flex w-2/12 pl-6 items-center">Alias</div>
          <div className="flex w-1/12 pl-6 items-center"></div>
        </div>
      </div>

      {state?.data?.data.map((device, index) => {
        return (
          <DevicesRow
            {...device}
            onDelete={() => {
              setDeviceToDelete(device)
            }}
            key={`device-${index}`}
            onShowDetails={() => {
              dispatch({
                type: SET_SELECTED_DEVICES_DATA,
                data: { ...device, index: index },
              })
            }}
          />
        )
      })}

      {state?.data?.data?.length < 1 && (
        <NoRecordsFound caption={'Devices Data not found'} loading={false} />
      )}

      <div className="py-6">
        <Pagination
          context={state?.data?.pagination}
          onClickNext={(e) => {
            currentUrl.current = e
            getDevicesData()
          }}
          onClickPrev={(e) => {
            currentUrl.current = e
            getDevicesData()
          }}
        />
      </div>
      {deleteMsg && (
        <ConfirmationPopup
          confirmTitle={`Are you sure want to Delete ${deviceToDelete.name}?`}
          confirmText={deleteMsg}
          onClose={() => {
            setDeviceToDelete({})
            setDeleteMsg('')
          }}
          onConfirm={() => {
            deleteDevice(deviceToDelete)
            setDeleteMsg('')
          }}
        />
      )}
    </div>
  )
}

export default Devices
